import {
  Alert,
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import React from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import { Link, useNavigate, useParams } from "react-router-dom";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LanguageIcon from "@mui/icons-material/Language";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import PeopleIcon from "@mui/icons-material/People";
import BadgeIcon from "@mui/icons-material/Badge";

const BusinessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useApiEp(
    `/partner/business-detail/${id}`
  );

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return <Alert variant="outlined">{getErrorResponse(error)}</Alert>;
  }

  // Format the timestamp to readable date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      <BackBtn />
      <Typography variant="h4" gutterBottom sx={{ mt: 2, mb: 3 }}>
        Business Details
      </Typography>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <BusinessIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" component="h2">
            {data.display_name || data.company}
          </Typography>
          <Chip
            label={data.active ? "Active" : "Inactive"}
            color={data.active ? "success" : "error"}
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem icon={<BadgeIcon />} label="Type" value={data.type} />
          <DetailItem icon={<EmailIcon />} label="Email" value={data.email} />
          <DetailItem
            icon={<PhoneIcon />}
            label="Contact"
            value={data.contact}
          />
          <DetailItem
            icon={<CurrencyExchangeIcon />}
            label="Currency"
            value={data.currency}
          />
          <DetailItem
            icon={<LanguageIcon />}
            label="Timezone"
            value={data.timezone}
          />
          <DetailItem
            icon={<PeopleIcon />}
            label="Company Size"
            value={data.companySize}
          />
          <DetailItem
            icon={<ScheduleIcon />}
            label="Created At"
            value={formatDate(data.created_at)}
          />
          <DetailItem
            icon={<ScheduleIcon />}
            label="Updated At"
            value={formatDate(data.updated_at)}
          />
        </Box>

        {data.project_ids && data.project_ids.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Project IDs:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {data.project_ids.map((projectId) => (
                <Chip
                  onClick={() => {
                    navigate(`/home/${projectId}/project-detail`);
                  }}
                  clickable
                  key={projectId}
                  label={projectId}
                  variant="outlined"
                />
              ))}
            </Box>
          </>
        )}
      </Paper>
      <Box mt={2} display={"flex"} justifyContent={"center"}>
        <Link to={`/home/${id}/projects-list`}>
          <Typography variant="h6">Projects List</Typography>
        </Link>
      </Box>
    </Container>
  );
};

// Helper component for consistent detail items
const DetailItem = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Box sx={{ color: "text.secondary", mr: 1 }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value || "N/A"}</Typography>
    </Box>
  </Box>
);

export default BusinessDetail;
